import {
  compileJsonSchema,
  SchemaObject,
  ValidateFunction,
} from '@pogokid/schema/dynamic'
import {
  compileValidationSchema,
  ValidateSchemaResult,
} from '@pogokid/schema'
import {
  EventType,
  MatchHandlers,
  PartialMatchHandlers,
  PayloadFor,
  SemanticEvent,
  ValidatePayloadResult,
} from './types'

export interface DynamicEnumSchemaOptions {
  enumSchema?: SchemaObject
  /** The discriminator property name, defaults to 'event_type' */
  discriminator?: string
}

/**
 * A schema wrapper for discriminated union types (semantic events).
 * Provides type-safe pattern matching, object building, and validation.
 *
 * @example
 * ```typescript
 * const schedulingSem = new DynamicEnumSchema<SchedulingSemanticDomainEvents>({
 *   enumSchema: { ...definitions.SchedulingSemanticDomainEvents, definitions }
 * });
 *
 * // Exhaustive pattern matching
 * const result = schedulingSem.match(event, {
 *   attendance_status_changed: (payload) => `Changed to ${payload.status_to}`,
 * });
 *
 * // Type-safe building
 * const event = schedulingSem.build('attendance_status_changed', {
 *   contact_id: 'uuid',
 *   // ... other fields
 * });
 * ```
 */
export class DynamicEnumSchema<Enum extends SemanticEvent> {
  protected options: DynamicEnumSchemaOptions
  protected compiledSchema: ValidateFunction<Enum> | null = null
  protected validateFn:
    | ((data: unknown) => ValidateSchemaResult<Enum>)
    | null = null
  protected discriminator: string

  constructor(options: DynamicEnumSchemaOptions) {
    this.options = options
    this.discriminator = options.discriminator ?? 'event_type'

    if (options.enumSchema) {
      this.compiledSchema = compileJsonSchema(
        options.enumSchema
      ) as ValidateFunction<Enum>
      this.validateFn = compileValidationSchema<Enum>(
        'DynamicEnumSchema',
        this.compiledSchema
      )
    }
  }

  /**
   * Type-safe exhaustive pattern matching.
   * Requires a handler for every event type in the union.
   *
   * @param event - The event to match
   * @param handlers - Object with a handler for each event_type, all handlers must return the same type
   * @returns The result of the matching handler
   *
   * @example
   * ```typescript
   * const result = schema.match(event, {
   *   attendance_status_changed: (payload) => {
   *     return `Attendance changed to ${payload.status_to}`;
   *   },
   *   schedule_created: (payload) => {
   *     return `Schedule ${payload.schedule_id} created`;
   *   }
   * });
   * ```
   */
  match<R>(
    event: SemanticEvent,
    handlers: MatchHandlers<Enum, R>
  ): R | void {
    const eventType = event[
      this.discriminator as 'event_type'
    ] as EventType<Enum>
    const handler = handlers[eventType]

    if (handler) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return handler((event as SemanticEvent).payload as any)
    }
  }

  /**
   * Type-safe exhaustive pattern matching which Errors if the event
   * is not part of the Enum.
   *
   * Requires a handler for every event type in the union.
   *
   * @param event - The event which must match the enum
   * @param handlers - Object with a handler for each event_type
   * @returns The result of the matching handler
   *
   * @example
   * ```typescript
   * const result = schema.match(event, {
   *   attendance_status_changed: (payload) => {
   *     return `Attendance changed to ${payload.status_to}`;
   *   },
   *   schedule_created: (payload) => {
   *     return `Schedule ${payload.schedule_id} created`;
   *   }
   * });
   * ```
   */
  strictMatch<R>(
    event: SemanticEvent,
    handlers: MatchHandlers<Enum, R>
  ): R {
    const eventType = event[
      this.discriminator as 'event_type'
    ] as EventType<Enum>
    const handler = handlers[eventType]

    if (!handler) {
      throw new Error(
        `No handler for event type: ${String(eventType)}`
      )
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return handler((event as SemanticEvent).payload as any)
  }

  /**
   * Non-exhaustive pattern matching with optional default handler.
   * Allows partial handlers and uses `_` property as fallback.
   *
   * @param event - The event to match
   * @param handlers - Object with optional handlers for each event_type
   * @returns The result of the matching handler, or undefined if no match
   *
   * @example
   * ```typescript
   * const handled = schema.matchPartial(event, {
   *   attendance_status_changed: (payload) => true,
   *   _: () => false // default handler
   * });
   * ```
   */
  matchPartial<R>(
    event: Enum,
    handlers: PartialMatchHandlers<Enum, R>
  ): R | undefined {
    const eventType = (event as SemanticEvent)[
      this.discriminator as 'event_type'
    ] as EventType<Enum>
    const handler = (handlers as MatchHandlers<Enum, R>)[
      eventType
    ]

    if (handler) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return handler((event as SemanticEvent).payload as any)
    }

    if (handlers._) {
      return handlers._(event)
    }

    return undefined
  }

  /**
   * Type-safe object construction with runtime validation.
   * Validates the payload against JSON schema at runtime.
   *
   * @param eventType - The event type literal
   * @param payload - The payload for this event type
   * @returns Properly typed event object
   * @throws Error if payload fails validation
   *
   * @example
   * ```typescript
   * const event = schema.build('attendance_status_changed', {
   *   contact_id: 'uuid',
   *   schedule_id: 'uuid',
   *   schedule_instance_id: null,
   *   recurrence_id: '2024-01-01T10:00:00Z',
   *   event_start: '2024-01-01T10:00:00Z',
   *   status_from: null,
   *   status_to: 'confirmed'
   * });
   * ```
   */
  build<E extends EventType<Enum>>(
    eventType: E,
    payload: PayloadFor<Enum, E>
  ): Extract<Enum, { event_type: E }> {
    const event = {
      [this.discriminator]: eventType,
      payload,
    } as Extract<Enum, { event_type: E }>

    // Runtime validation if schema is available
    if (this.validateFn) {
      const result = this.validateFn(event)
      if (!result.isValid) {
        const errorMessages = result.errors
          ? Object.entries(result.errors)
              .map(([field, msg]) => `${field}: ${msg}`)
              .join(', ')
          : 'Unknown validation error'
        throw new Error(
          `Invalid payload for ${String(
            eventType
          )}: ${errorMessages}`
        )
      }
    }

    return event
  }

  /**
   * Runtime type guard that narrows the event type.
   *
   * @param event - The event to check
   * @param eventType - The expected event type
   * @returns Type predicate narrowing the event
   *
   * @example
   * ```typescript
   * if (schema.is(event, 'attendance_status_changed')) {
   *   // event is narrowed to attendance_status_changed variant
   *   console.log(event.payload.status_to);
   * }
   * ```
   */
  is<E extends EventType<Enum>>(
    event: Enum,
    eventType: E
  ): event is Extract<Enum, { event_type: E }> {
    return (
      (event as SemanticEvent)[
        this.discriminator as 'event_type'
      ] === eventType
    )
  }

  /**
   * Validate a complete event against the JSON schema.
   *
   * @param data - Unknown data to validate
   * @returns Validation result with typed cleanedValues
   *
   * @example
   * ```typescript
   * const result = schema.validate(unknownData);
   * if (result.isValid) {
   *   // result.cleanedValues is typed as Enum
   *   console.log(result.cleanedValues.event_type);
   * }
   * ```
   */
  validate(data: unknown): ValidateSchemaResult<Enum> {
    if (!this.validateFn) {
      return {
        isValid: false,
        cleanedValues: data as Enum,
        errors: { _: 'No schema configured for validation' },
      }
    }
    return this.validateFn(data)
  }

  /**
   * Validate a payload for a specific event type.
   * Wraps the payload in the event structure and validates.
   *
   * @param eventType - The event type to validate against
   * @param payload - The payload to validate
   * @returns Validation result with typed payload
   *
   * @example
   * ```typescript
   * const result = schema.validatePayload('attendance_status_changed', payload);
   * if (result.isValid) {
   *   // result.cleanedValues is typed as AttendanceStatusChanged
   * }
   * ```
   */
  validatePayload<E extends EventType<Enum>>(
    eventType: E,
    payload: unknown
  ): ValidatePayloadResult<PayloadFor<Enum, E>> {
    const event = {
      [this.discriminator]: eventType,
      payload,
    }

    const result = this.validate(event)

    return {
      isValid: result.isValid,
      cleanedValues: (result.isValid
        ? (result.cleanedValues as SemanticEvent).payload
        : payload) as PayloadFor<Enum, E>,
      errors: result.errors,
    }
  }

  /**
   * Get all valid event types from the schema.
   * Extracts event types from the oneOf/anyOf schema structure.
   *
   * @returns Array of event type strings
   *
   * @example
   * ```typescript
   * const types = schema.eventTypes();
   * // ['attendance_status_changed', 'schedule_created', ...]
   * ```
   */
  eventTypes(): EventType<Enum>[] {
    if (!this.options.enumSchema) {
      return []
    }

    const schema = this.options.enumSchema

    // Handle oneOf/anyOf union schemas
    const variants = schema.oneOf || schema.anyOf || [schema]

    const types: EventType<Enum>[] = []

    for (const variant of variants) {
      if (variant.properties?.[this.discriminator]?.const) {
        types.push(
          variant.properties[this.discriminator]
            .const as EventType<Enum>
        )
      } else if (
        variant.properties?.[this.discriminator]?.enum
      ) {
        types.push(
          ...(variant.properties[this.discriminator]
            .enum as EventType<Enum>[])
        )
      }
    }

    return types
  }
}
